import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, Users, UserCheck, Share2, Clock, KeyRound, Activity, CheckCircle2, FileBadge, AlertTriangle, Sparkles, Ban, BarChart3, BookOpen, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

const TILES = [
  { key: "total_students", label: "Total Students", icon: Users },
  { key: "direct_registrations", label: "Direct Registrations", icon: UserCheck },
  { key: "marketing_referred_registrations", label: "Marketing-Referred", icon: Share2 },
  { key: "free_signups", label: "Free Signups", icon: Sparkles },
  { key: "paid_signups", label: "Paid (Pool) Signups", icon: KeyRound },
  { key: "blocked_licenses", label: "Blocked (Pool Exhausted)", icon: Ban },
  { key: "awaiting_activation", label: "Awaiting Activation", icon: Clock },
  { key: "licenses_active", label: "Licenses Active", icon: KeyRound },
  { key: "assessments_in_progress", label: "Assessments In Progress", icon: Activity },
  { key: "assessments_submitted", label: "Assessments Submitted", icon: CheckCircle2 },
  { key: "reports_ready", label: "Reports Ready", icon: FileBadge },
  { key: "marketing_executives_active", label: "Marketing Executives Active", icon: UserCog },
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = () => api.get("/admin/dashboard").then((res) => setData(res.data));
  useEffect(() => {
    load();
    api.get("/admin/settings").then((res) => setSettings(res.data));
  }, []);

  const toggleFreeForAll = async (checked) => {
    try {
      await api.post("/admin/settings", { free_for_all: checked });
      setSettings((s) => ({ ...s, free_for_all: checked }));
      toast.success(checked ? "Free-for-all is now ON — ME license pools are bypassed" : "Free-for-all is now OFF — ME license pools apply");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update setting");
    }
  };

  const runReset = async () => {
    if (confirmText !== "RESET") return;
    setResetting(true);
    try {
      await api.post("/admin/system/reset-all", { confirm: "RESET" });
      toast.success("All platform data has been reset");
      setResetOpen(false);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset data");
    } finally {
      setResetting(false);
    }
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1" data-testid="admin-dashboard-heading">Super Admin Overview</h1>
      <p className="text-sm text-slate-500 mb-6">Platform-wide visibility across students, referrals and activations.</p>

      <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50/60 p-5 mb-8">
        <h3 className="font-heading text-sm font-semibold text-indigo-900 mb-1">Platform Settings — Free-for-All Toggle</h3>
        <p className="text-xs text-indigo-700/80 mb-4">
          When Free-for-All is ON, all student registrations bypass Marketing Executive license pools entirely
          (unlimited free signups, tagged "free"). When OFF, each Marketing Executive draws from their own license
          pool (default 3) — one registration consumes one license; if their pool hits zero, new registrations still
          succeed but stay blocked/pending (tagged "paid") until the pool is topped up.
        </p>
        <div className="flex items-center gap-3" data-testid="free-for-all-toggle-row">
          <Switch
            data-testid="free-for-all-switch"
            checked={!!settings?.free_for_all}
            onCheckedChange={toggleFreeForAll}
          />
          <span className="text-sm font-semibold text-indigo-900">
            Free-for-All Mode is currently <span className={settings?.free_for_all ? "text-emerald-600" : "text-slate-500"}>{settings?.free_for_all ? "ON" : "OFF"}</span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {TILES.map((t) => (
          <div key={t.key} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-metric-${t.key}`}>
            <t.icon className="h-4 w-4 text-indigo-500 mb-2" />
            <p className="text-2xl font-bold text-slate-900 font-mono">{data ? data[t.key] ?? 0 : "—"}</p>
            <p className="text-xs text-slate-500 mt-1">{t.label}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-8">
        Financial tiles (Total Collected, Payment Success, Refunds, Gateway Settlements) will appear here once payment integration is enabled — visible only to Super Admin.
      </p>

      <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5">
        <div className="flex items-center gap-2 text-red-700 font-semibold text-sm mb-1">
          <AlertTriangle className="h-4 w-4" /> Danger Zone
        </div>
        <p className="text-xs text-red-600 mb-4">
          Permanently wipe all students, marketing executives, sessions, reports and analytics data. Super Admin accounts and cached sample reports are preserved. This cannot be undone.
        </p>
        <Button
          size="sm"
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-100"
          data-testid="reset-all-data-button"
          onClick={() => setResetOpen(true)}
        >
          Reset All Data
        </Button>
      </div>

      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) setConfirmText(""); }}>
        <DialogContent data-testid="reset-all-dialog">
          <DialogHeader>
            <DialogTitle>Reset all platform data?</DialogTitle>
            <DialogDescription>
              This deletes every student, marketing executive, session, license, report and analytics record. Super Admin accounts and cached sample reports stay intact. Type RESET below to confirm.
            </DialogDescription>
          </DialogHeader>
          <Label>Type RESET to confirm</Label>
          <Input value={confirmText} data-testid="reset-all-confirm-input" onChange={(e) => setConfirmText(e.target.value)} placeholder="RESET" />
          <DialogFooter>
            <Button
              data-testid="reset-all-confirm-button"
              disabled={confirmText !== "RESET" || resetting}
              className="bg-red-600 hover:bg-red-700"
              onClick={runReset}
            >
              {resetting ? "Resetting…" : "Permanently Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
